import type { AdJourneyGraph, JourneyNode } from "./ad-journey-guide";

type NodeStyle = {
  fillColor: string;
  outlineColor: string;
  outlineWidth: number;
};

export default class AdJourneyRenderer {
  private canvas: HTMLCanvasElement | null = null;

  private container: HTMLElement | null = null;

  private ctx: CanvasRenderingContext2D | null = null;

  // Constants for diagram
  private static readonly periwinkleColor = "#555fff";

  private static readonly whiteColor = "#ffffff";

  private static readonly clearFill = "#848BF7";

  private static readonly currentFill = "#F7BACF";

  // Radius of each circle
  private static readonly nodeRadius = 17;

  // Vertical spacing between circles
  private static readonly nodeVerticalSpacing = 90;

  // Horizontal distance for text labels
  private static readonly labelOffset = 10;

  private static readonly labelFont = "bold 13px Poppins";

  // Width of connecting lines
  private static readonly standardLineWidth = 3.2;

  private static readonly highlightedLineWidth = 5.0;

  private static readonly nodeStyles: Record<string, NodeStyle> = {
    UNVISITED: {
      fillColor: AdJourneyRenderer.clearFill,
      outlineColor: AdJourneyRenderer.periwinkleColor,
      outlineWidth: AdJourneyRenderer.standardLineWidth,
    },
    VISITED: {
      fillColor: AdJourneyRenderer.clearFill,
      outlineColor: AdJourneyRenderer.whiteColor,
      outlineWidth: AdJourneyRenderer.standardLineWidth,
    },
    CURRENT: {
      fillColor: AdJourneyRenderer.currentFill,
      outlineColor: AdJourneyRenderer.whiteColor,
      outlineWidth: AdJourneyRenderer.highlightedLineWidth,
    },
  };

  private static nodeLabelAdjustments(label: string): string {
    // Sometimes we don't want to use the node label provided by ViewMaster
    let newLabel = label;
    if (label === "Image") {
      newLabel = "Static";
    } else if (label === "Playable") {
      newLabel = "Interactive";
    }

    return newLabel;
  }

  public init(canvas: HTMLCanvasElement, containerDiv: HTMLElement): void {
    this.container = containerDiv;
    this.canvas = canvas;
    this.ctx = canvas ? canvas.getContext("2d") : null;
  }

  public draw(graph: AdJourneyGraph | null): void {
    if (this.container === null || this.canvas === null || this.ctx === null) {
      return;
    }

    // Get device pixel ratio
    const dpr = window.devicePixelRatio || 1;

    // Set canvas dimensions to match the container
    const containerWidth = this.container.clientWidth;
    const containerHeight = this.container.clientHeight;

    // Scale the canvas resolution for high DPI
    this.canvas.width = containerWidth * dpr;
    this.canvas.height = containerHeight * dpr;

    // Set CSS dimensions for the canvas
    this.canvas.style.width = `${containerWidth}px`;
    this.canvas.style.height = `${containerHeight}px`;

    // Scale the drawing context
    this.ctx.scale(dpr, dpr);

    // Clear the canvas
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    // If we have no graph data, stop after clearing
    if (graph === null) {
      return;
    }

    // Predict the height of the diagram
    const diagramHeight =
      AdJourneyRenderer.nodeVerticalSpacing * (graph.nodes.length - 1);

    // Starting Y position (center it - does not guarantee it will fit!)
    const startY = (containerHeight - diagramHeight) / 2;

    // Assign the locations for the nodes in advance
    let indentNext = false;
    const centerX = containerWidth / 2; // X position to center the row
    for (let i = 0; i < graph.nodes.length; i++) {
      const node: JourneyNode = graph.nodes[i];

      if (indentNext) {
        node.alternative = true;
      }
      node.x = node.alternative ? centerX + 60 : centerX;
      node.y = startY + i * AdJourneyRenderer.nodeVerticalSpacing;

      // This is really not reliable beyond PageTurner
      indentNext = node.connections.size > 1;
    }

    // Draw the nodes
    this.drawConnections(graph);
    for (let i = 0; i < graph.nodes.length; i++) {
      this.drawNode(graph.nodes[i]);
    }
  }

  private drawConnections(graph: AdJourneyGraph): void {
    // We need to draw all the highlighted connections last so they're on top
    const highlightedConnections = [];

    for (let i = 0; i < graph.nodes.length; i++) {
      const node: JourneyNode = graph.nodes[i];
      for (const connectedNode of node.connections) {
        // If it's highlighted, save for later
        if (connectedNode.fromNode === node) {
          highlightedConnections.push(
            this.drawConnection.bind(this, node, connectedNode)
          );
        } else {
          // Draw it now
          this.drawConnection(node, connectedNode);
        }
      }
    }

    // Now draw the highlighted connections
    highlightedConnections.forEach((call) => {
      call.call(this);
    });
  }

  private drawNode(node: JourneyNode): void {
    if (this.ctx === null) {
      return;
    }

    const style: NodeStyle = AdJourneyRenderer.nodeStyles[node.state];

    // Draw the ring
    this.ctx.lineWidth = style.outlineWidth;
    this.ctx.strokeStyle = style.outlineColor;
    this.ctx.fillStyle = style.fillColor;
    this.ctx.beginPath();
    this.ctx.arc(node.x, node.y, AdJourneyRenderer.nodeRadius, 0, Math.PI * 2);
    this.ctx.fill();
    this.ctx.stroke();

    // Draw text label to the left of the circle
    this.ctx.font = AdJourneyRenderer.labelFont;
    this.ctx.textAlign = node.alternative ? "left" : "right";
    const labelX =
      node.x -
      (AdJourneyRenderer.nodeRadius + AdJourneyRenderer.labelOffset) *
        (node.alternative ? -1 : 1); // Position to the left
    this.ctx.fillStyle = style.outlineColor;
    this.ctx.fillText(
      AdJourneyRenderer.nodeLabelAdjustments(node.label),
      labelX,
      node.y + 3
    ); // Slight vertical adjustment for centering text
  }

  private drawConnection(node: JourneyNode, connectedNode: JourneyNode): void {
    if (this.ctx === null) {
      return;
    }

    // The connection line should be highlighted if the current node is where we arrived from
    this.ctx.strokeStyle =
      connectedNode.fromNode === node
        ? AdJourneyRenderer.whiteColor
        : AdJourneyRenderer.periwinkleColor;

    // Constants for arcs
    const verticalOffset = AdJourneyRenderer.nodeVerticalSpacing / 2; // Amount to move straight down from Point A
    const cornerRadius = 15; // Radius of the rounded corners
    const dashedLineSettings = [8, 3];

    this.ctx.lineWidth = AdJourneyRenderer.standardLineWidth;
    this.ctx.beginPath();
    this.ctx.moveTo(node.x, node.y);

    if (node.alternative) {
      // Fancy dashed arc
      this.ctx.setLineDash(dashedLineSettings);
      const segment1 = { x: node.x, y: node.y + verticalOffset }; // Downward movement
      const segment2 = { x: connectedNode.x + cornerRadius, y: segment1.y }; // Horizontal movement
      const segment3 = { x: connectedNode.x, y: connectedNode.y }; // Final downward movement

      // Segment 1: Draw straight down
      this.ctx.lineTo(node.x, segment1.y - cornerRadius);

      // Curve 1: From downward line to horizontal line
      this.ctx.arcTo(
        segment1.x,
        segment1.y, // Control point for arc
        segment2.x,
        segment2.y, // End point of the arc
        cornerRadius // Radius of the curve
      );

      // Segment 2: Draw horizontally
      this.ctx.lineTo(segment2.x, segment2.y);

      // Curve 2: From horizontal line to downward line
      this.ctx.arcTo(
        segment2.x - cornerRadius,
        segment2.y, // Control point for arc
        segment3.x,
        segment3.y - cornerRadius, // End point of the arc
        cornerRadius // Radius of the curve
      );

      // Segment 3: Draw straight down to Point B
      this.ctx.lineTo(segment3.x, segment3.y);
    } else if (connectedNode.alternative) {
      // Fancy dashed arc
      this.ctx.setLineDash(dashedLineSettings);
      const segment1 = { x: node.x, y: node.y + verticalOffset }; // Downward movement
      const segment2 = { x: connectedNode.x, y: segment1.y }; // Horizontal movement
      const segment3 = { x: connectedNode.x, y: connectedNode.y }; // Final downward movement

      // Segment 1: Draw straight down
      this.ctx.lineTo(node.x, segment1.y - cornerRadius);

      // Curve 1: From downward line to horizontal line
      this.ctx.arcTo(
        segment1.x,
        segment1.y, // Control point for arc
        segment2.x - cornerRadius,
        segment2.y, // End point of the arc
        cornerRadius // Radius of the curve
      );

      // Segment 2: Draw horizontally
      this.ctx.lineTo(segment2.x - cornerRadius, segment2.y);

      // Curve 2: From horizontal line to downward line
      this.ctx.arcTo(
        segment2.x,
        segment2.y, // Control point for arc
        segment3.x,
        segment3.y - cornerRadius, // End point of the arc
        cornerRadius // Radius of the curve
      );

      // Segment 3: Draw straight down to Point B
      this.ctx.lineTo(segment3.x, segment3.y);
    } else {
      // Regular vertical line
      this.ctx.lineTo(connectedNode.x, connectedNode.y);
    }

    this.ctx.stroke();
    this.ctx.setLineDash([]);
  }
}
